import {
  type AnyRouteMatch,
  type ParsedLocation,
  redirect,
} from '@tanstack/react-router';
import type { SignupSearchParams } from 'router/search/signup';
import type {
  RoutePaths,
  RoutePathsWithTrailingSlashes,
  StepName,
} from 'router/types';

import type { AppRouterContext } from 'providers/AppRouter';
import type { Lead } from 'providers/LeadProvider';
import { setAmplitudeUserId } from 'services/amplitude';
import {
  type GetLeadResponse,
  readLeadQueryCache,
  resolveLeadQueryVariables,
} from 'services/partnersSelfSignup/signup';
import { isDOMAvailable } from 'utils/browser';
import { setLeadExperimentAttributes } from 'utils/experimentation';

import { buildMetaTags } from './seo/buildMetaTags';
import type { ValidateLeadStatusReturn } from './validateLeadStatus';

export type SignupSubRouteRouteContext = AppRouterContext & {
  queryVariables: {
    leadId: string;
    businessEmail: string; // Finds the hash of the Partner's challenge when a request is sent
    oppId?: string;
  };
};

export type SignupSubRouteValidation = {
  validate?: (leadResponse: GetLeadResponse) => boolean | Promise<boolean>;
  redirectTarget?: Parameters<typeof redirect>[0]['to'];
};

/**
 * Function to be executed before loading the _signup layout sub-routes.
 * It checks if the lead has a valid selfSignUpId and redirects to the home page if not.
 *
 * @param {Object} params - The parameters for the function.
 * @param {AppRouterContext} params.context - The context of the app router.
 * @param {ParsedLocation<SignupSearchParams>} params.location - The location object containing search parameters.
 * @throws Will throw a redirect to the home page if the lead does not have a valid selfSignUpId.
 */
export async function beforeLoadSignupSubRoute({
  context,
  preload,
  cause,
  validation = {
    redirectTarget: '/',
  },
  validateLeadStatus,
}: {
  context: AppRouterContext;
  location: ParsedLocation<SignupSearchParams>;
  preload: boolean;
  cause: AnyRouteMatch['cause'];
  validateLeadStatus?: (
    leadStatus: Lead['status'],
    leadStatusReason: Lead['statusReason'],
  ) => ValidateLeadStatusReturn;
  validation?: SignupSubRouteValidation;
}) {
  const variables = resolveLeadQueryVariables(context.queryClient);
  const lead = readLeadQueryCache(context.queryClient, variables);

  if (preload) {
    // Avoid redirects and errors when preloading routes
    return {
      queryVariables: {
        ...variables,
        businessEmail: lead?.lead?.businessEmail ?? 'unknown',
      },
    };
  }

  const { localization, snackbarContext, growthbook, rum } = context;

  // Only on route entry: a landing (reload, direct URL, email-validation
  // recovery) always enters, which is when GrowthBook's in-memory attributes
  // can be stale or absent. 'stay' re-runs (e.g. a router.invalidate() from a
  // language change) are excluded so the correction can never revert an
  // in-screen attribute write the lead hasn't caught up with yet, like a
  // just-picked Country on Business Details.
  if (cause === 'enter') {
    await setLeadExperimentAttributes({
      growthbook,
      lead: lead?.lead,
      rum,
    });
  }

  setAmplitudeUserId(lead?.lead?.id);

  const { t } = localization;

  if (!lead?.selfSignUpId) {
    snackbarContext.showSnackbar({ message: t('generic_error') });
    throw redirect({ to: '/' });
  }

  const isValid = (await validation?.validate?.(lead)) ?? true;

  if (!isValid) {
    snackbarContext.showSnackbar({ message: t('generic_error') });
    throw redirect({ to: validation?.redirectTarget ?? '/' });
  }

  const validationResult = validateLeadStatus?.(
    lead.lead.status,
    lead.lead.statusReason,
  );

  if (validationResult?.shouldRedirect) {
    throw redirect(validationResult.redirectOptions);
  }

  return {
    queryVariables: {
      ...variables,
      businessEmail: lead?.lead.businessEmail ?? 'unknown',
    },
  };
}

/**
 * Resolves tracking parameters by converting the input value into a string.
 *
 * - If the input is an array with at least one element, the first element is converted to a string.
 * - If the input is a single value, it is converted to a string.
 *
 * @returns A string representation of the resolved value
 */
export function resolveTrackingParams(
  val: number | (string | number)[] | string | undefined | null,
): string | undefined {
  return Array.isArray(val) && val.length > 0
    ? val?.[0]?.toString()
    : val?.toString();
}

/**
 * Function to be executed before loading the _signup layout sub-routes.
 *
 * If the load fails due to module not found, it may mean a new version of
 * the build was deployed and the user's browser is still using an old version.
 * If this happens, the old version in the user's browser would have an outdated URL to the lazy module.
 * In that case, we want to attempt one window refresh to get the latest.
 */
export function isModuleNotFoundError(error: Error): boolean {
  if (typeof error?.message !== 'string') return false;

  const isNameMatch =
    error.name === 'ChunkLoadError' || error.name === 'InternalError';

  const isMessageMatch =
    // Chrome
    error.message.startsWith('Failed to fetch dynamically imported module') ||
    error.message.startsWith('Failed to load module script') ||
    // Firefox
    error.message.startsWith('error loading dynamically imported module') ||
    // Safari
    error.message.startsWith('Importing a module script failed') ||
    error.message.startsWith('Failed to load resource') ||
    // Cloudfront fallback
    error.message.includes('non-JavaScript MIME type') ||
    error.message.includes('not a valid JavaScript MIME type') ||
    error.message.includes('disallowed MIME type');

  return isNameMatch || isMessageMatch;
}

export const routePathsToStepName = {
  '/': 'welcome',
  '/activities': 'activities',
  '/pricing': 'pricing',
  '/status': 'status',
  '/status/$statusScreen': 'status',
  '/success': 'success',
  '/offer': 'offer',
  '/offer/decline': 'offer-decline',
  '/email-validation': 'email-validation',
  '/gym/business-details': 'gym-business-details',
  '/gym/tax-details': 'gym-tax-details',
  '/opportunity': 'opportunity',
  '/opportunity/$oppId': 'opportunity',
  '/opportunity/$oppId/terms-and-conditions': 'opportunity',
  '/opportunity/$oppId/terms-and-conditions/decline': 'opportunity',
  '/opportunity/$oppId/terms-and-conditions/decline/success': 'success',
  '/opportunity/$oppId/email-validation': 'email-validation',
  '/opportunity/$oppId/success': 'success',
  // Step 1, same as `/` — see docs/adr/013-explicit-step-1-registration-for-start-routes.md
  '/start/$businessType': 'welcome',
  '/start/others': 'welcome',
} as const satisfies Record<RoutePaths, StepName | null>;

const routePathsWithTrailingSlashToStepName = {
  '/': routePathsToStepName['/'],
  '/activities/': routePathsToStepName['/activities'],
  '/pricing/': routePathsToStepName['/pricing'],
  '/status/': routePathsToStepName['/status'],
  '/status/$statusScreen/': routePathsToStepName['/status/$statusScreen'],
  '/success/': routePathsToStepName['/success'],
  '/offer/': routePathsToStepName['/offer'],
  '/offer/decline/': routePathsToStepName['/offer/decline'],
  '/email-validation/': routePathsToStepName['/email-validation'],
  '/gym/business-details/': routePathsToStepName['/gym/business-details'],
  '/gym/tax-details/': routePathsToStepName['/gym/tax-details'],
  '/opportunity/': routePathsToStepName['/opportunity'],
  '/opportunity/$oppId/': routePathsToStepName['/opportunity'],
  '/opportunity/$oppId/terms-and-conditions/':
    routePathsToStepName['/opportunity'],
  '/opportunity/$oppId/terms-and-conditions/decline/':
    routePathsToStepName['/opportunity/$oppId/terms-and-conditions/decline'],
  '/opportunity/$oppId/terms-and-conditions/decline/success/':
    routePathsToStepName[
      '/opportunity/$oppId/terms-and-conditions/decline/success'
    ],
  '/opportunity/$oppId/email-validation/':
    routePathsToStepName['/opportunity/$oppId/email-validation'],
  '/opportunity/$oppId/success/':
    routePathsToStepName['/opportunity/$oppId/success'],
  '/start/$businessType/': routePathsToStepName['/start/$businessType'],
  '/start/others/': routePathsToStepName['/start/others'],
} as const satisfies Record<RoutePathsWithTrailingSlashes, StepName | null>;

/**
 * Type guard that checks if the given pathname is a valid key of the `routePathsToStepName` mapping.
 *
 * @param pathname - The pathname to check, which can be a string or null.
 * @returns `true` if the pathname is not null and exists as a key in `routePathsToStepName`, otherwise `false`.
 */
function isRoutePath(pathname: string | null): pathname is RoutePaths {
  return pathname !== null && pathname in routePathsToStepName;
}

/**
 * Determines if the given pathname is a valid route path with trailing slashes.
 *
 * Checks whether the provided `pathname` is not `null` and exists as a key in the
 * `routePathsWithTrailingSlashToStepName` mapping. If so, narrows the type of `pathname`
 * to `RoutePathsWithTrailingSlashes`.
 *
 * @param pathname - The pathname string to check, or `null`.
 * @returns `true` if `pathname` is a valid route path with trailing slashes; otherwise, `false`.
 */
function isRoutePathWithTrailingSlashes(
  pathname: string | null,
): pathname is RoutePathsWithTrailingSlashes {
  return pathname !== null && pathname in routePathsWithTrailingSlashToStepName;
}

/**
 * Retrieves the step name corresponding to a given path name.
 *
 * This function checks if the provided path matches a known route path or a route path with trailing slashes,
 * and returns the associated step name. If the path does not match any known route, it returns `null`.
 *
 * @typeParam P - The type of the path, which can be a string or null.
 * @param path - The path name to resolve to a step name.
 * @returns The step name associated with the path, or `null` if no match is found.
 */
export function getStepNameFromPathName<const P extends string | null>(
  path: P,
) {
  if (isRoutePath(path)) return routePathsToStepName[path];
  if (isRoutePathWithTrailingSlashes(path))
    return routePathsWithTrailingSlashToStepName[path];

  return null;
}

/**
 * Resolves the step name from the deepest route match's `fullPath` (the
 * static route pattern), not `location.pathname`: for a param route (e.g.
 * `/status/$statusScreen`) the resolved pathname (`/status/not-eligible`) is
 * never a key of the step-name map, which used to silently degrade the
 * event category to 'unknown'. The pattern always is.
 *
 * Taking the last match as the deepest one is a documented ordering contract,
 * not an implementation detail: TanStack Router's `useParentMatches` is
 * specified as "from the root down to the immediate parent" and
 * `useChildMatches` as "down to the leaf-most match" — i.e. match arrays run
 * root → leaf, so `.at(-1)` is the leaf-most (most specific) match.
 * See https://tanstack.com/router/latest/docs/api/router/useParentMatchesHook
 * and https://tanstack.com/router/latest/docs/api/router/useChildMatchesHook
 *
 * Kept pure so navigation tests can run it against the real
 * `router.state.matches`; `useCurrentStepName` is a thin adapter over it.
 */
export function getStepNameFromMatches(
  matches: ReadonlyArray<{ fullPath: string }>,
) {
  return getStepNameFromPathName(matches.at(-1)?.fullPath ?? null);
}

/**
 * Maps step names to their corresponding translation prefixes.
 *
 * This object is used to associate each step in the signup flow
 * with a specific translation key prefix, which can be used for internationalization
 * and localization of UI text.
 *
 * The keys represent step identifiers (as returned by `getStepNameFromPathName`),
 * and the values are the translation prefix strings.
 *
 * @remarks
 * The mapping includes an empty string key for the default or root step.
 */
const stepNameTranslationPrefix = {
  activities: 'activities_page',
  welcome: 'starting_page',
  pricing: 'pricing',
  // TODO: request generic status title
  status: 'lead_status.unsupported_business_type',
  success: 'success_page.html',
  offer: 'real_time_offer_page',
  'offer-decline': 'real_time_offer_page.decline',
  'email-validation': 'email_validation',
  '': 'html',
  'gym-business-details': 'details',
  'gym-tax-details': 'details_tax',
  opportunity: 'opportunity.tc',
} as const satisfies Record<
  Exclude<ReturnType<typeof getStepNameFromPathName>, null> | '',
  string
>;

/**
 * Generates meta information for the signup sub-route header, such as the page title,
 * based on the current route's pathname and localization context.
 *
 * Pass it directly (`head: headSignupSubRoute`) — wrapping it in an arrow makes
 * the property context-sensitive, which collapses the inference of the other
 * `createFileRoute` options (`beforeLoad` context, `loaderDeps`, ...) declared
 * after it. The parameter is deliberately structural: it demands only what the
 * function reads, and constraining `fullPath` to the step-name map keys makes
 * attaching it to an unmapped route a compile error.
 *
 * @param ctx - The header function options containing route match information and localization context.
 * @returns An object containing meta information for the route, including a localized title.
 */
export function headSignupSubRoute(ctx: {
  match: {
    fullPath: RoutePaths | RoutePathsWithTrailingSlashes;
    context: AppRouterContext;
  };
}) {
  const { context } = ctx.match;
  const { t, language } = context.localization;
  const stepName = getStepNameFromPathName(ctx.match.fullPath);
  const baseKey = stepNameTranslationPrefix[stepName ?? ''];

  return {
    meta: [
      ...buildMetaTags({
        title: t(`${baseKey}.title`),
        description: t(`${baseKey}.subtitle`),
        language,
        url: isDOMAvailable()
          ? `https://${globalThis.location.host}${ctx.match.fullPath}`
          : undefined,
        keywords:
          'partners, partners signup, wellness programs, corporate wellness, employee benefits',
      }),
    ],
  };
}
